// Path: src/routes/Routes.tsx
// Enhanced redirect logic for admin dashboard, ensured proper role-based routing.
// Optimized for SEO with <Helmet> and mobile responsiveness. 06/07/25 8:34 PM +07
import React from 'react';
import { Routes as RouterRoutes, Route, Navigate } from 'react-router-dom';
import WithAuthentication from '../components/auth/session/WithAuthentication';
import PublicLayout from '../components/Layout/PublicLayout';
import { useUserStore } from '../stores/UserStore';
import { observer } from 'mobx-react';
import { ability } from '../ability';
import AdminPanel from '../components/admin/AdminPanel';
import AdminDashboard from '../components/admin/AdminDashboard';
import VerifyUsers from '../components/admin/Users/VerifyUsers';
import UserDetails from '../components/admin/Users/UserDetails';
import { OrdersList, OrderDetails } from '../components/admin/Orders';
import LiveSupport from '../components/admin/LiveSupport';
import Privacy from '../components/aols/privacy/Privacy';
import License from '../components/aols/privacy/license/License';
import OLSpublicUse from '../components/aols/privacy/OLSpublicUse';
import OLSsPrivacy from '../components/aols/privacy/OLSsPrivacy';
import OLSLMprivacy from '../components/aols/privacy/OLSLMprivacy';
import OlsmePrivacy from '../components/aols/privacy/OlsmePrivacy';
import OlsDataProvision from '../components/aols/privacy/OlsDataProvision';
import OLSaeo from '../components/aols/aeo/OLSaeo/OLSaeo';
import OLShealth from '../components/aols/aeo/OLShealth/OLShealth';
import Packages from '../components/aols/aeo/OLShealth/Packages';
import Login from '../components/auth/Login';
import Register from '../components/auth/register/Register';
import OLSSRegister from '../components/auth/register/OLSSRegister';
import OLSMERegister from '../components/auth/register/OLSMERegister';
import OLSLMRegister from '../components/auth/register/OLSLMRegister';
import Verification from '../components/auth/verification/Verification';
import InitiProcess from '../components/auth/verification/InitiProcess';
import PasswordReset from '../components/auth/register/PasswordReset';
import SignUpConfirmation from '../components/auth/register/SignUpConfirmation';
import Contact from '../components/aols/aeo/Contact/Contact';
import Stat from '../components/aols/profile/Stat';
import AH from '../components/aols/profile/A.H';
import AA from '../components/aols/profile/A.A';
import RWHPage from '../components/aols/profile/R.W.H';
import Estate from '../components/aols/Estate/Estate';
import PropertyDetail from '../components/aols/Estate/PropertyDetail';
import Olsme from '../components/aols/olsme/olsme';
import OlsmeSub from '../components/aols/olsme/olsmeSub';
import AOLSLMasset from '../components/aols/aOLSLMasset/aOLSLMasset';
import LightScience from '../components/aols/aeo/LightScience/LightScience';
import Venus from '../components/aols/aeo/Venus/Venus';
import OLSupdates from '../components/aols/aeo/OLSupdates/OLSupdates';
import OLSStudentProspect from '../components/aols/OLSstudent/OLSstudentProspect';
import NotFound from './NotFound';
import { Helmet } from 'react-helmet-async';
import ErrorBoundary from '../components/dev/utils/ErrorBoundary';
import './Routes.css';
import AboutAA from '../components/aols/profile/AboutAA';
import AboutRWH from '../components/aols/profile/AboutRWH';
import OLSinventions from '../components/aols/aeo/OLSinventions/OLSinventions';
import OLSCalendarTime from '../components/aols/aeo/OLSCalendarTime/OLSCalendarTime';
import OLS from '../components/auth/OLS';
import { UserRole } from '@constants/UserEnums';
import HomeLayout from '../components/dashboard/HomeLayout';
import Profile from '../components/dashboard/Profile';
import Support from '../components/dashboard/Support';
import EvaluationS from '../components/dashboard/Account/EvaluationS';
import Intro from '../components/dashboard/Intro';
import Referral from '../components/referral/Referral';
import ReferralP from '../components/referral/ReferralP';
import withVerification from '../components/auth/verification/WithVerification';
import { getFirestore, doc, getDoc, setDoc } from 'firebase/firestore';

const ProtectedEvaluationS = withVerification(EvaluationS);

const AppRoutes = observer(() => {
  const userStore = useUserStore();
  const db = getFirestore();

  if (!userStore.isLoaded) {
    return <div>Loading...</div>;
  }

  const isVerified = userStore.verified === true || userStore.verified === "true" || userStore.verified === "Verified";
  const user = userStore.user;

  const handleRedirect = (path: string, reason: string) => {
    console.log(`AppRoutes: Redirecting to ${path} - Reason: ${reason}`);
    return <Navigate to={path} replace />;
  };

  const handleUserLoginRedirect = async () => {
    if (!user) {
      return handleRedirect("/login", "No user logged in");
    }

    if (userStore.userRole === UserRole.ADMIN) {
      return handleRedirect("/admin/users/all", "Admin user detected, routing to dashboard");
    }

    const prospectRef = doc(db, 'prospects', user.uid);
    const prospectDoc = await getDoc(prospectRef);

    if (prospectDoc.exists()) {
      const userRef = doc(db, 'users', user.uid);
      await setDoc(userRef, { ...prospectDoc.data(), userStatus: userStore.userStatus || 'prospect', initiStatus: userStore.initiStatus || 'pending' }, { merge: true });
    }

    const userStatus = userStore.userStatus || 'prospect';
    const initiStatus = userStore.initiStatus || 'pending';

    if (!isVerified) {
      return handleRedirect("/verification", "User not verified");
    } else if (initiStatus === 'pending') {
      return handleRedirect("/initi", "Initi status pending");
    } else if (userStatus === 'active') {
      return handleRedirect("/home/ols", "User status active");
    } else {
      return handleRedirect("/initi", "Default redirect for verified users with pending initi");
    }
  };

  return (
    <>
      <Helmet>
        <title>OneLightSystem OLS - Sun Light Meditation</title>
        <meta name="description" content="Explore OneLightSystem OLS for Sun Light Meditation education and join our radiant Light Civilization. Register at olsme.com/OLSStudentProspect." />
        <meta name="keywords" content="Sun Light Meditation, OLS meditation education, join OLS, Light Civilization" />
        <link rel="canonical" href="https://www.olsme.com/" />
      </Helmet>
      <RouterRoutes>
        {/* Public Routes */}
        <Route path="/referral" element={<PublicLayout><ReferralP /></PublicLayout>} />
        <Route path="/login" element={<PublicLayout><Login /></PublicLayout>} />
        <Route path="/register" element={<PublicLayout><Register /></PublicLayout>} />
        <Route path="/OLShealth" element={<PublicLayout><OLShealth /></PublicLayout>} />
        {/* CORRECTED LOCATION: This route is public and must be at the top level. */}
        <Route path="/OLShealth/packages" element={<PublicLayout><Packages /></PublicLayout>} />
       <Route path="/referral" element={<PublicLayout><ReferralP /></PublicLayout>} />
        <Route path="/login" element={<PublicLayout><Login /></PublicLayout>} />
        <Route path="/register" element={<PublicLayout><Register /></PublicLayout>} />
        <Route path="/register/olssregister" element={<PublicLayout><OLSSRegister /></PublicLayout>} />
        <Route path="/register/olsmeregister" element={<PublicLayout><OLSMERegister /></PublicLayout>} />
        <Route path="/register/olslmregister" element={<PublicLayout><OLSLMRegister /></PublicLayout>} />
        <Route path="/passwordreset" element={<PublicLayout><PasswordReset /></PublicLayout>} />
        <Route path="/signupconfirmation" element={<PublicLayout><SignUpConfirmation /></PublicLayout>} />
        <Route path="/Contact" element={<PublicLayout><Contact /></PublicLayout>} />
        <Route path="/privacy" element={<PublicLayout><Privacy /></PublicLayout>} />
        <Route path="/license" element={<PublicLayout><License /></PublicLayout>} />
        <Route path="/public-use" element={<PublicLayout><OLSpublicUse /></PublicLayout>} />
        <Route path="/olsdataprovision" element={<PublicLayout><OlsDataProvision /></PublicLayout>} />
        <Route path="/Stat" element={<PublicLayout><Stat /></PublicLayout>} />
        <Route path="/ah" element={<PublicLayout><AH /></PublicLayout>} />
        <Route path="/aa" element={<PublicLayout><AA /></PublicLayout>} />
        <Route path="/about-aa" element={<PublicLayout><AboutAA /></PublicLayout>} />
        <Route path="/rwh" element={<PublicLayout><RWHPage /></PublicLayout>} />
        <Route path="/about-rwh" element={<PublicLayout><AboutRWH /></PublicLayout>} />
        <Route path="/OLSsPrivacy" element={<PublicLayout><OLSsPrivacy /></PublicLayout>} />
        <Route path="/OLSLMprivacy" element={<PublicLayout><OLSLMprivacy /></PublicLayout>} />
        <Route path="/OlsmePrivacy" element={<PublicLayout><OlsmePrivacy /></PublicLayout>} />
        <Route path="/estate" element={<PublicLayout><Estate /></PublicLayout>} />
        <Route path="/estate/:id" element={<PublicLayout><PropertyDetail /></PublicLayout>} />
        <Route path="/olsme" element={<PublicLayout><Olsme /></PublicLayout>} />
        <Route path="/olsmeSub" element={<PublicLayout><OlsmeSub /></PublicLayout>} />
        <Route path="/aOLSLMasset" element={<PublicLayout><AOLSLMasset /></PublicLayout>} />
        <Route path="/lightScience" element={<PublicLayout><LightScience /></PublicLayout>} />
        <Route path="/venus" element={<PublicLayout><Venus /></PublicLayout>} />
        <Route path="/olsupdates/*" element={<PublicLayout><OLSupdates /></PublicLayout>} />
        <Route path="/OLSStudentProspect" element={<PublicLayout><OLSStudentProspect /></PublicLayout>} />
        <Route path="/OLSaeo" element={<PublicLayout><OLSaeo /></PublicLayout>} />
        <Route path="/OLShealth" element={<PublicLayout><OLShealth /></PublicLayout>} />
        <Route path="/OLSinventions" element={<PublicLayout><OLSinventions /></PublicLayout>} />
        <Route path="/OLSCalendarTime" element={<PublicLayout><OLSCalendarTime /></PublicLayout>} />
        <Route path="/" element={<PublicLayout><Login /></PublicLayout>} />


        {/* Protected Routes */}
        <Route path="/home/*" element={<HomeLayout />}>
          <Route index element={handleUserLoginRedirect()} />
          <Route path="ols" element={<OLS />} />
          {/* ... other home routes */}
        </Route>
        
        <Route path="/admin/*">
          <Route
            index
            element={
              user ? (
                <WithAuthentication
                  authCondition={(authUser) => authUser && (ability.can('access', 'Admin panel') || userStore.userRole === UserRole.ADMIN)}>
                  <AdminPanel />
                </WithAuthentication>
              ) : handleRedirect("/login", "No user logged in for admin route")
            }
          />
          <Route path="users/all" element={<AdminDashboard />} />
          <Route path="users/verify" element={<VerifyUsers />} />
          <Route path="user/:id" element={<UserDetails />} />
          <Route path="orders" element={<OrdersList />} />
          <Route path="orders/:orderId" element={<OrderDetails />} />
          <Route path="live-support" element={<LiveSupport />} />
          {/* REMOVED: This route was incorrectly nested here. */}
          <Route path="*" element={<div>Route not found in AdminRoutes - Path: {window.location.pathname}</div>} />
        </Route>

        <Route path="/initi" element={<InitiProcess />} />
        <Route path="/verification" element={<Verification />} />
        
        <Route path="*" element={<NotFound />} />
      </RouterRoutes>
    </>
  );
});

export default AppRoutes;